"use client";

import React from "react";
import { Facebook, Instagram, Linkedin, Twitter, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
function FooterMenuLinks() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="22831" className="flex flex-col gap-3">
        {[1, 2, 3].map(i => <div key={i} className="h-4 w-24 bg-muted/10 animate-pulse rounded" />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) return null;
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-muted-foreground hover:text-primary transition-colors text-sm font-medium">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-4 bg-primary/10 border border-primary/20 rounded-sm">
        <p className="text-primary text-sm font-medium">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      {}
      <FormField control={control} name="first_name" render={({
      field
    }) => <FormItem>
            <FormLabel className="sr-only">First Name</FormLabel>
            <FormControl>
              <Input placeholder="First Name (Optional)" {...field} className="bg-transparent border-0 border-b border-white/10 rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/50 text-sm" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="sr-only">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="Email Address" {...field} className="bg-transparent border-0 border-b border-white/10 rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/50 text-sm" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="agree_terms" rules={{
      required: "You must agree to the terms"
    }} render={({
      field: {
        value,
        onChange,
        ...field
      }
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0">
            <FormControl>
              <input type="checkbox" checked={value} onChange={onChange} className="mt-1 size-4 rounded-sm border-white/20 bg-transparent text-primary focus:ring-primary/40 focus:ring-offset-background" {...field} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-[10px] text-muted-foreground font-normal leading-tight">
                I agree to receive promotional emails. You can unsubscribe at any time.
              </FormLabel>
              <FormMessage />
            </div>
          </FormItem>} />

      {submitError && <p className="text-destructive text-xs">{submitError}</p>}

      <Button type="submit" disabled={isSubmitting} className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-[0_0_20px_rgba(249,115,22,0.3)] transition-all font-bold uppercase tracking-wider text-xs" variant="default">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Joining...
          </> : "Join The Split"}
      </Button>
    </Form>;
}
export default function MainSiteFooter() {
  const currentYear = new Date().getFullYear();
  const businessName = "HalfOrder";
  const manifesto = "HalfOrder is the social way to eat in Toronto. Share meals, split costs, and connect with nearby foodies. Built for the city, made in Toronto.";
  const shopLinks = [{
    label: "Home",
    to: "/HomePage"
  }, {
    label: "How it works",
    to: "/HowItWorksPage"
  }, {
    label: "Features",
    to: "/FeaturesPage"
  }, {
    label: "Download App",
    to: "/DownloadPage"
  }];
  return <footer id="halforder-footer" className="dark bg-background text-foreground pt-20 pb-10 border-t border-white/5">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-16">
          
          {}
          <div className="space-y-6">
            <Link to="/HomePage" className="block">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1611703/img/logo_white.png" alt={businessName} className="h-8 w-auto object-contain" />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-xs">
              {manifesto}
            </p>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/5 border border-white/10 rounded-sm">
              <span className="size-2 bg-primary rounded-full animate-pulse" />
              <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/70">Made in Toronto</span>
            </div>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="text-sm font-black tracking-tighter uppercase text-primary">Quick Links</h3>
            <ul className="space-y-3">
              {shopLinks.map((link, i) => <li key={i} data-index={i}>
                  <Link to={link.to} className="text-muted-foreground hover:text-primary transition-colors text-sm font-medium">
                    {link.label}
                  </Link>
                </li>)}
            </ul>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="text-sm font-black tracking-tighter uppercase text-primary">Support</h3>
            <MenuProvider menu_id="17" className="w-full">
              <FooterMenuLinks />
            </MenuProvider>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="text-sm font-black tracking-tighter uppercase text-primary">The Social Split</h3>
            <p className="text-muted-foreground text-sm leading-snug">
              Subscribe for exclusive group deals and new dining spots in your neighborhood.
            </p>
            
            <FormProvider formKey="newsletter_signup_home" formId="newsletter_signup_home" sectionName="footer-newsletter" defaultValues={{
            first_name: "",
            email: "",
            agree_terms: false
          }} validationRules={{
            email: {
              required: true,
              pattern: "email"
            },
            agree_terms: {
              required: true
            }
          }} submitText="Join" successMessage="Welcome to the movement!">
              <NewsletterForm />
            </FormProvider>
          </div>
        </div>

        <Separator className="bg-white/5 mb-8" />

        {}
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-6">
            <Link to="https://facebook.com" newTab aria-label="Facebook" className="text-muted-foreground hover:text-primary transition-colors">
              <Facebook className="size-5" />
            </Link>
            <Link to="https://instagram.com" newTab aria-label="Instagram" className="text-muted-foreground hover:text-primary transition-colors">
              <Instagram className="size-5" />
            </Link>
            <Link to="https://twitter.com" newTab aria-label="Twitter" className="text-muted-foreground hover:text-primary transition-colors">
              <Twitter className="size-5" />
            </Link>
            <Link to="https://linkedin.com" newTab aria-label="LinkedIn" className="text-muted-foreground hover:text-primary transition-colors">
              <Linkedin className="size-5" />
            </Link>
          </div>

          <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8 text-[10px] font-mono uppercase tracking-widest text-muted-foreground/60">
            <p>&copy; {currentYear} {businessName}. Built for Toronto.</p>
            <div className="flex gap-6">
              <Link to="#" className="hover:text-primary transition-colors">Privacy</Link>
              <Link to="#" className="hover:text-primary transition-colors">Terms</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>;
}